import React, { MouseEventHandler, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { IconButton } from '@videoblocks/design-system';
import CloseIcon from '@videoblocks/react-icons/Close';

interface RenderInputComponentProps extends React.InputHTMLAttributes<HTMLInputElement> {
    defaultValue?: string;
    useNewSearchUI?: boolean;
    showClearButton?: boolean;
    onClearSearchTerm?: MouseEventHandler<HTMLButtonElement>;
    showSuggestions?: boolean;
    useWarpSearchUI?: boolean;
    popupId?: string;
}

const AutoSuggestInput = React.forwardRef<HTMLInputElement, RenderInputComponentProps>(
    (
        {
            defaultValue,
            className,
            placeholder = 'Search...',
            useNewSearchUI,
            showClearButton,
            onClearSearchTerm,
            onFocus,
            onBlur,
            showSuggestions,
            useWarpSearchUI = false,
            popupId = 'navSearchBox-autoSuggestionList',
            ...rest
        }: RenderInputComponentProps,
        ref,
    ) => {
        const [dynamicPlaceholder, setDynamicPlaceholder] = useState(placeholder);

        if (useNewSearchUI) {
            return (
                <div className="flex flex-row items-center">
                    <input
                        id="search-input"
                        type="text"
                        autoComplete="off"
                        ref={ref}
                        aria-label="Input Search: "
                        data-public={true}
                        placeholder={dynamicPlaceholder}
                        className={twMerge(
                            'unifiedNav-searchInput rounded-l-xl-35 border-none shadow-none font-semibold h-full outline-hidden w-full text-lg px-3',
                            className,
                        )}
                        defaultValue={defaultValue}
                        onFocus={(e) => {
                            setDynamicPlaceholder('');
                            onFocus?.(e);
                        }}
                        onBlur={(e) => {
                            setDynamicPlaceholder(placeholder);
                            onBlur?.(e);
                        }}
                        role="combobox"
                        aria-expanded={!!showSuggestions}
                        aria-controls={popupId}
                        aria-haspopup="listbox"
                        aria-autocomplete="list"
                        {...rest}
                    />
                    <div className="p-4">
                        <button
                            id="clear-search-term-button"
                            type="button"
                            onClick={onClearSearchTerm}
                            className={showClearButton ? 'w-4 h-4 border-none' : 'invisible'}
                        >
                            <CloseIcon />
                        </button>
                    </div>
                </div>
            );
        }
        return (
            <>
                <input
                    autoComplete="off"
                    ref={ref}
                    aria-label="Input Search: "
                    data-public={true}
                    placeholder={placeholder}
                    className={twMerge(
                        'unifiedNav-searchInput rounded-l-xl-35 border-none shadow-none font-semibold h-full outline-hidden w-full py-2 text-sm px-3',
                        className,
                        showClearButton && 'pr-8',
                    )}
                    defaultValue={defaultValue}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    role="combobox"
                    aria-expanded={!!showSuggestions}
                    aria-controls={popupId}
                    aria-haspopup="listbox"
                    aria-autocomplete="list"
                    {...rest}
                />
                {useWarpSearchUI && showClearButton && (
                    <div className="p-2 absolute top-0 right-0 flex self-center h-full">
                        <IconButton
                            icon={<CloseIcon />}
                            variant="transparent"
                            id="clear-search-term-button"
                            type="button"
                            onClick={onClearSearchTerm}
                            className={'p-0 w-auto h-auto border-none rounded-xs'}
                            aria-label="Clear search term"
                            title="Clear Search Term"
                        />
                    </div>
                )}
            </>
        );
    },
);

AutoSuggestInput.displayName = 'AutoSuggestInput';

export default AutoSuggestInput;
